import debounce from 'debounce'
import { User } from '@graphics/gfx-common/dist/types'
import { Language } from '@graphics/gfx-common/dist/constants'
import {
  FormObjectType,
  ITypeaheadObject,
  ISelectInput,
  EditionLanguage,
} from '../types/pack'
import { IOption } from '../types/common'
import { distinctObjectsArray, trace } from './common'
import { safeGet, typeaheadHostUrl, tagsHostUrl, groupsHostUrl } from './http'

const slugTermsUrl = `${typeaheadHostUrl}/slug-terms`
const locationUrl = `${typeaheadHostUrl}/locations`
const topicCodesUrl = `${typeaheadHostUrl}/topic-codes`
const tagsUrl = `${tagsHostUrl}/tags`
const editorRolesUrl = `${groupsHostUrl}/roles/find-by-prefix`

export const getSluglineSuggestions = async (
  inputValue: string
): Promise<IOption[]> => {
  const { slugTerms, events } = await safeGet(
    `${slugTermsUrl}?query=${encodeURIComponent(
      inputValue
    )}&take=20&language=en`
  )
  return [...events, ...slugTerms].map((label, value) => ({
    value: `${value}`,
    label,
  }))
}

export interface ISluglineEvent {
  slugline: string
  topicCodes: string[]
}

// TODO: maybe move interfaces out of services to types
export interface IValidateSluglineResponse {
  passed: boolean
  messages: string[]
  event: ISluglineEvent | null
  suggestedEvent: ISluglineEvent | null
}

export const validateSlugline = (language: EditionLanguage) => (
  slug: string
): Promise<IValidateSluglineResponse> =>
  safeGet(
    `${slugTermsUrl}/${encodeURIComponent(slug)}?language=${language ||
      Language.English}`
  )

export const resolveAdditionalTopicCodes = ({
  topicCodes,
}: {
  topicCodes: string[]
}) => (topicCodes ? Promise.all(topicCodes.map(getRelatedTopicsById)) : [])

export const applyAdditionalTopicCodes = async (
  event: ISluglineEvent,
  callback: (values: FormObjectType[]) => void
) => {
  const topics = await resolveAdditionalTopicCodes(event)
  if (topics.length > 0) {
    const additionalCodes = (topics as ITypeaheadObject[]).flatMap(
      ({ additionalCodesExpanded }) => additionalCodesExpanded || []
    )
    callback(
      [...topics, ...additionalCodes]
        .map(topicsTypeaheadToFormAdapter)
        .map((topic) => ({
          ...topic,
          slugline: event.slugline,
        }))
    )
  }
}

export const typeaheadToFormAdapter = (
  response: ITypeaheadObject
): FormObjectType => ({
  ...response,
  value: response.rcsId,
  name: (response.n2000 && response.n2000[0]) || response.rcsId,
  label: response.shortName,
})

// TODO: Refactoring , move to typeaheadToFormAdapter
export const topicsTypeaheadToFormAdapter = (
  response: ITypeaheadObject
): FormObjectType => {
  const label = (response.n2000 && response.n2000[0]) || response.rcsId
  return {
    ...response,
    value: response.rcsId,
    name: label,
    label,
  }
}

export const formToTypeaheadAdapter = ({
  value,
  name,
  label,
  ...rest
}: FormObjectType): ITypeaheadObject => rest

export const getLocationsSuggestions = (
  inputValue: string,
  callback: (items: FormObjectType[]) => void
): void => {
  safeGet(`${locationUrl}?query=${encodeURIComponent(inputValue)}&take=20`)
    .then((res) => callback(res.map(typeaheadToFormAdapter)))
    .catch((err) => {
      trace(err, callback([]))
    })
}

export const debouncedGetLocationsSuggestions = debounce(
  getLocationsSuggestions,
  250
)

export const getLocationPath = (
  location: FormObjectType
): Promise<FormObjectType> => safeGet(`${locationUrl}/${location.rcsId}`)

export const handleLocationChange = (
  callback: (location: FormObjectType) => void
) => (location: FormObjectType): void => {
  getLocationPath(location).then((res) => {
    callback(typeaheadToFormAdapter(res))
  })
}

export const getTopicsSuggestions = (
  inputValue: string,
  callback: (items: FormObjectType[]) => void
): void => {
  safeGet(`${topicCodesUrl}?query=${encodeURIComponent(inputValue)}&take=20`)
    .then((res) => callback(res.map(typeaheadToFormAdapter)))
    .catch((err) => {
      trace(err)
      callback([])
    })
}

export const debouncedGetTopicsSuggestions = debounce(getTopicsSuggestions, 250)

export const getRelatedTopics = (
  topic: FormObjectType
): Promise<ITypeaheadObject> => safeGet(`${topicCodesUrl}/${topic.rcsId}`)

export const getRelatedTopicsById = (
  rcsId: string
): Promise<ITypeaheadObject> => safeGet(`${topicCodesUrl}/${rcsId}`)

const distinctTopics = distinctObjectsArray('rcsId')

export const getTagsSuggestions = (groupId: string) => (
  inputValue: string,
  callback: (items: ISelectInput[]) => void
): void => {
  safeGet(
    `${tagsUrl}?group=${groupId}&query=${encodeURIComponent(
      inputValue
    )}&deprecated=false&skip=0&take=30`
  )
    .then((res) =>
      callback(
        res.items.map(({ id, text }) => ({
          id,
          value: text,
          label: text,
        }))
      )
    )
    .catch((err) => {
      trace(err)
      callback([])
    })
}

export const createDebouncedGetTagsSuggestions = (groupId: string) =>
  debounce(getTagsSuggestions(groupId), 250)

export const handleTopicsChange = (
  callback: (topics: FormObjectType[]) => void
) => (value?: FormObjectType[]): void => {
  const topics = value || []
  const existingTopics = topics.filter(
    ({ conceptType }) => conceptType !== undefined
  )
  const newTopic = topics.find(({ conceptType }) => conceptType === undefined)
  if (!!newTopic) {
    getRelatedTopics(newTopic).then((updatedNewTopic) => {
      const relevantTopics = (updatedNewTopic.additionalCodesExpanded || [])
        .filter(Boolean)
        .map(topicsTypeaheadToFormAdapter)
      callback(
        distinctTopics(
          [
            ...existingTopics,
            topicsTypeaheadToFormAdapter(updatedNewTopic),
            ...relevantTopics,
          ].filter(Boolean)
        )
      )
    })
  } else {
    callback(existingTopics)
  }
}

export const extendTopics = (
  topics: FormObjectType[],
  newTopics: FormObjectType[]
): FormObjectType[] => distinctTopics([...topics, ...newTopics])

export const getEditorRoleSuggestions = (
  inputValue: string,
  callback: (items: ISelectInput[]) => void
): void => {
  safeGet(
    `${editorRolesUrl}/${encodeURIComponent('Graphics ' + inputValue)}?take=20`
  )
    .then((res: { items: User[] }) => {
      callback(
        res.items.map(
          ({ id, name }: User): ISelectInput => ({
            id,
            value: name,
            label: name,
          })
        )
      )
    })
    .catch((err) => {
      trace(err)
      callback([])
    })
}

export const debouncedGetEditorRoleSuggestions = debounce(
  getEditorRoleSuggestions,
  250
)
